<script lang="ts">
	import Lead from "#lib/Lead.svelte";
	import theTime from "@ishavskraft/the-time";
	import lang from "#lib/langStore.js";
	import { getSanityPictureObject, SanityPicture as Picture, Button } from "@ishavskraft/shared";
	import type { Author, SanityPicture as Image } from "#lib/types/shared.js";

	let {
		title,
		lead,
		author,
		publishedAt,
		lastReviewed,
		image,
	}: {
		title: string;
		lead: string;
		author: Author[] | null;
		publishedAt: string;
		/** Editor-set "Sist gjennomgått"; also drives schema.org `dateModified`. */
		lastReviewed?: string | null;
		image: Image | null | undefined;
	} = $props();

	// The image spans `mx-edge-x` (92vw) until `max-w-prose` caps it at 700px —
	// --font-size-body is 20px below laptop.
	const imageSizes = "(min-width: 763px) 700px, 92vw";

	const formatDate = (date: string) =>
		theTime(date).toLocaleDateString($lang || "nb", {
			day: "numeric",
			month: "long",
			year: "numeric",
		});

	// Shown only when it adds something. Both fields are Sanity `date`
	// (YYYY-MM-DD), so comparing the date part as text is exact and keeps time
	// zones out of it entirely.
	const updatedAt = $derived(
		lastReviewed && publishedAt && lastReviewed.slice(0, 10) > publishedAt.slice(0, 10)
			? lastReviewed
			: null
	);

	// Authors come from `author[]->` without an expanded asset, so the URL is
	// built here rather than in the loader — that also covers the live preview.
	// Two of the twelve authors have no image; those render as name only.
	const avatar = (a: Author) =>
		a.image?.asset?._ref || a.image?.asset?._id
			? getSanityPictureObject(a.image, {
					sizes: [
						[64, 64],
						[128, 128],
					],
					fit: "crop",
				})
			: null;
</script>

<header>
	<div class="mx-edge-x flex flex-col-reverse *:max-w-prose *:w-full">
		<div class="laptop:py-2 mx-auto">
			<h1 class="leading-tight hyphens-manual mt-0">
				{title}
			</h1>
			<Lead
				class={[
					"mt-0 max-w-prose leading-normal landscape:hyphens-manual",
					"contain-content relative before:content-[''] before:absolute before:w-2 before:inset-0 before:bg-lysning pl-4 font-600 before:rounded-[.5rem]",
				]}>{lead}</Lead
			>

			<div class="mt-8 mb-2 grid gap-3">
				<p class="byline max-w-prose my-0 text-[var(--fg-muted)] font-500">
					{#if updatedAt}
						<span class="block"
							>Sist oppdatert <time datetime={updatedAt} class="dt-updated"
								>{formatDate(updatedAt)}</time
							></span
						>
					{/if}
					<span class={["block", updatedAt && "font-400 text-sm"]}
						>Publisert <time datetime={publishedAt} class="dt-published"
							>{formatDate(publishedAt)}</time
						></span
					>
				</p>
				{#if author && author.length}
					<address class="byline max-w-prose not-italic my-0">
						<span class="sr-only">Av</span>

						<ul class="list-none p-0 m-0 flex flex-col gap-1">
							{#each author as a (a._id)}
								{@const pic = avatar(a)}
								{#snippet person()}
									<span class="flex gap-x-2 items-center">
										{#if pic}
											<!-- Decorative: the name it links to sits right beside it -->
											<span class="avatar">
												<Picture circle pictureObject={pic} sizes="3.5rem" alt="" />
											</span>
										{/if}
										<span class="leading-tight">
											{a.name}
											<small class="block text-[var(--fg-muted)] font-500">{a.title}</small>
										</span>
									</span>
								{/snippet}

								<li>
									{#if a?.profileLink}
										<Button
											link
											textStart
											class="p-0 min-w-0"
											href={a.profileLink}
											target="_blank"
											rel="noopener noreferrer"
										>
											{@render person()}
										</Button>
									{:else}
										<span class="flex gap-x-2 items-center">{@render person()}</span>
									{/if}
								</li>
							{/each}
						</ul>
					</address>
				{/if}
			</div>
		</div>

		{#if !!image}
			<figure class="max-w-prose mx-auto">
				<Picture
					pictureObject={image}
					sizes={imageSizes}
					loading="eager"
					fetchpriority="high"
					class="u-photo block mx-auto rounded-2xl max-h-[75vh]"
				/>
				{#if image.credit}
					<figcaption class="text-sm mt-1 text-end mx-2">Bilde: {image.credit}</figcaption>
				{/if}
			</figure>
		{/if}
	</div>
</header>

<style lang="postcss">
	.avatar {
		/* Picture reads its width from this in an unlayered rule, so a `w-*`
		   utility can't reach it. */
		--picture-width: 3.5rem;
		/* Not inline: an inline img carries descender space in its line box. */
		display: flex;
		flex: none;
	}
</style>
